// The atlas cells of the point of interest marks (R2714, mockup round 114).
//
// `poi:<set>:<kind>:<state>:<m>` is one mark of a set (`poi-marks.ts`), and
// `poi:hex` the white hex that the REACH set tints for the water of a
// current gate. A cell is resolved on demand, so a page bakes only the set
// the reader picked and the states its points stand in. Each mark is baked
// in its own inks, so the quad draws it WHITE and carries the fade alone.

import type { AtlasSpec } from '../fast/gfx/atlas';
import { HEX_SIZE, RESOURCES } from '../gen/balance';
import { ACCENT_COLOR, CHART, MAP_PALETTE, NEUTRAL_COLOR, NPC_INK } from '../gen/presentation';
import { POI_HEX_KEY, paintPoiHex, paintPoiMark, readPoiCellKey, type PoiInks } from './poi-marks';

/** The ink of one resource of the catalog, by key. */
function resourceInk(key: string): number {
  return RESOURCES.find((r) => r.key === key)?.color ?? NEUTRAL_COLOR;
}

/** The inks of round 114: the palette text, the water black of the NPC
 *  edge, the chrome accent, the isotope and credits hues, the NPC ink and
 *  the neutral. */
export const POI_INKS: PoiInks = {
  pale: MAP_PALETTE.text,
  deep: NPC_INK.edge,
  accent: ACCENT_COLOR,
  iso: resourceInk('isotopes'),
  credit: resourceInk('credits'),
  npc: NPC_INK.color,
  neutral: NEUTRAL_COLOR,
};

/** The half side of a mark cell, in world px: the widest mark of the five
 *  sets (the arrow of a gate at full flood, the REACH cell) fits in it. */
export const POI_CELL_HALF = 26;

/** One cell of the marks, or null when the key is not one. */
export function poiSpecFor(key: string): AtlasSpec | null {
  const fig = CHART.pieces.points_of_interest.fig;
  if (key === POI_HEX_KEY) {
    const h = HEX_SIZE + 1;
    return { key, scale: fig.bake_scale, box: { x: -h, y: -h, w: 2 * h, h: 2 * h }, draw: paintPoiHex };
  }
  const k = readPoiCellKey(key);
  if (!k) return null;
  const h = POI_CELL_HALF;
  return {
    key,
    scale: fig.bake_scale,
    box: { x: -h, y: -h, w: 2 * h, h: 2 * h },
    draw(ctx) { paintPoiMark(ctx, k.set, k.kind, k.state, k.m, POI_INKS, fig); },
  };
}
